import enUs from 'date-fns/locale/en-US';
import * as React from 'react';
import * as ReactDatePickerNS from 'react-datepicker';
import type { ReactDatePickerProps } from 'react-datepicker';
import type ReactDatePickerType from 'react-datepicker';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DatePicker = ((ReactDatePickerNS as any).default?.default ?? (ReactDatePickerNS as any).default ?? ReactDatePickerNS) as typeof ReactDatePickerType;

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import styles from './styles.module.scss';

interface OwnProps {
	start: Date;
	end: Date;
	onChange: (selected: Date, startDate: Date, endDate: Date) => void;
	isPopperFixed?: boolean;
	isPopperInline?: boolean;
	maxDate?: Date;
	minDate?: Date;
	maxDays?: number;
}

type Props = OwnProps;

const POPPER_MODIFIERS = [
	{
		name: 'preventOverflow',
		options: {
			boundary: 'clippingParents',
		},
	},
] as unknown as ReactDatePickerProps['popperModifiers'];

const FIXED_POPPER_PROPS: ReactDatePickerProps['popperProps'] = {
	strategy: 'fixed',
};

const CustomPicker: React.FC<Props> = (props) => {
	const {
		start,
		end,
		onChange,
		isPopperFixed = false,
		isPopperInline = false,
		maxDate,
		minDate,
		maxDays,
	} = props;

	const handleSelectStart = React.useCallback((date: Nullable<Date>) => {
		const _date = date ?? new Date();
		const resolvedEnd = (maxDays && TimeUtils.getDiff(end, _date, 'days') > maxDays) ? TimeUtils.addDays(_date, maxDays - 1).toDate() : end;
		onChange(_date, _date, resolvedEnd);
	}, [maxDays, end, onChange]);

	const handleSelectEnd = React.useCallback((date: Nullable<Date>) => {
		const _date = date ?? new Date();
		const resolvedStart = (maxDays && TimeUtils.getDiff(_date, start, 'days') > maxDays) ? TimeUtils.addDays(_date, -(maxDays - 1)).toDate() : start;
		onChange(resolvedStart, resolvedStart, _date);
	}, [maxDays, onChange, start]);

	return (
		<div className={styles['date-picker-wrapper']}>
			<div className={styles['date-picker-wrapper__medium-picker']}>
				<DatePicker
					className={styles.picker}
					dateFormat={TimeUtils.datePickerFormat(TimeFormat.DATE_ONLY)}
					disabledKeyboardNavigation
					dropdownMode="select"
					endDate={end}
					locale={enUs}
					maxDate={end}
					minDate={minDate}
					onChange={handleSelectStart}
					popperModifiers={POPPER_MODIFIERS}
					popperPlacement={isPopperInline ? 'right' : undefined}
					popperProps={isPopperFixed ? FIXED_POPPER_PROPS : undefined}
					selected={start}
					selectsStart={true}
					showMonthDropdown={true}
					showYearDropdown={true}
					startDate={start}
					todayButton="Today"
				/>
			</div>
			<span className={styles['date-picker-dash']}>-</span>
			<div className={styles['date-picker-wrapper__medium-picker']}>
				<DatePicker
					className={styles.picker}
					dateFormat={TimeUtils.datePickerFormat(TimeFormat.DATE_ONLY)}
					disabledKeyboardNavigation
					dropdownMode="select"
					endDate={end}
					locale={enUs}
					maxDate={maxDate}
					minDate={start}
					onChange={handleSelectEnd}
					popperModifiers={POPPER_MODIFIERS}
					popperPlacement={isPopperInline ? 'left' : undefined}
					popperProps={isPopperFixed ? FIXED_POPPER_PROPS : undefined}
					selected={end}
					selectsEnd={true}
					showMonthDropdown={true}
					showYearDropdown={true}
					startDate={start}
					todayButton="Today"
				/>
			</div>
			<div className={styles['calendar-icon']}>
				<span className="icon-calendar" />
			</div>
		</div>
	);
};

export default React.memo(CustomPicker);
